import React from "react";
import styled, { css } from "styled-components";
import get from "lodash/get";
import debounce from "lodash/debounce";
import { withRouter } from "react-router-dom";
import { compose } from "react-apollo";
import { Shortcuts } from "react-shortcuts";
import Avatar from "../Avatar";
import Button from "../Button";
import { TextArea } from "../forms";
import {
  COMMENT_AVATAR_SIZE as AVATAR_SIZE,
  COMMENT_AVATAR_PADDING as AVATAR_PADDING
} from "../../settings/metrics";
import * as colors from "../../settings/colors";
import withCurrentUser from "../../hocs/withCurrentUser";

const TAB_PADDING = 10;

const CommentForm = styled.form`
  display: flex;
  margin: 24px 0;
`;

const FormHeader = styled.div`
  font-size: 14px;
  line-height: 1;
  color: ${colors.H2_TEXT};
  background: ${colors.SIDEBAR_BG};
  border: 1px solid ${colors.DIVIDER};
  padding: ${AVATAR_SIZE - TAB_PADDING * 2 - 14 - 1}px 8px 0;
  border-radius: 4px 4px 0 0;

  /* TODO: Something? I can't remember */

  b {
    color: ${colors.H1_TEXT};
    font-weight: 600;
  }
`;

const FormTab = styled.button.attrs({
  type: "button",
  role: "tab",
  "aria-selected": props => props.active
})`
  color: ${colors.H2_TEXT};
  padding: ${TAB_PADDING}px 10px;
  margin-right: 4px;
  font-size: 14px;
  line-height: 1;
  border: none;
  background: transparent;
  border-width: 1px;
  border-style: solid solid none solid;
  border-radius: 3px 3px 0 0;
  border-color: transparent;
  cursor: pointer;

  &:hover,
  &:focus,
  &:active {
    color: ${colors.H1_TEXT};
  }

  ${({ isPublic }) =>
    !isPublic &&
    css`
      color: #74674b;
    `} ${({ active, isPublic }) =>
    active &&
    css`
      color: ${colors.H1_TEXT};
      background: white;
      border-color: ${colors.DIVIDER};

      ${!isPublic &&
        css`
          background: #fffcf5;
          border-color: #d9cbae;
        `};
    `};
`;

const FormCommentBody = styled.div`
  border-style: none solid solid solid;
  border-width: 1px;
  border-color: ${colors.DIVIDER};
  border-top: 0;
  padding: 8px;
  font-size: 14px;
  line-height: 19px;
  border-radius: 0 0 4px 4px;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 7px;
`;

const FormBody = styled.div`
  position: relative;
  flex: 1;
  margin-left: ${AVATAR_PADDING}px;

  &:before,
  &:after {
    position: absolute;
    top: ${(AVATAR_SIZE - 14) / 2}px;
    right: 100%;
    left: -15px;
    display: block;
    width: 0;
    height: 0;
    pointer-events: none;
    content: " ";
    border-color: transparent;
    border-style: solid solid outset;
  }

  &:before {
    border-width: 8px;
    border-right-color: ${colors.DIVIDER};
  }

  &:after {
    margin-top: 1px;
    margin-left: 2px;
    border-width: 7px;
    border-right-color: ${colors.SIDEBAR_BG};
  }

  ${({ isPublic }) =>
    !isPublic &&
    css`
      &:before {
        border-right-color: #d9cbae;
      }

      &:after {
        border-right-color: #f5e9d1;
      }

      ${FormHeader} {
        color: #74674b;
        border-color: #d9cbae;
        background: #f5e9d1;

        b {
          color: #53462b;
        }
      }

      ${FormCommentBody} {
        color: #53462b;
        border-color: #d9cbae;
        background: #fffcf5;

        ${TextArea} {
          color: #53462b;
          border-color: #d9cbae;
          background: #fffcf5;
        }
      }
    `};
`;

class CommentFormContainer extends React.Component {
  constructor(props) {
    super(props);

    const defaultState = {
      submitting: false
    };

    if (this.props.comment) {
      this.state = {
        ...defaultState,
        body: this.props.comment.body,
        isPublic: this.props.comment.public
      };
    } else {
      this.state = {
        ...defaultState,
        body: "",
        isPublic: true
      };
    }
  }

  componentDidMount = () => {
    this.getStateFromLocalStorage();
  };

  handleShortcuts = (action, event) => {
    switch (action) {
      case "SUBMIT":
        this.onSubmit();
        break;
      default:
        return null;
    }
  };

  isValid = () => {
    const { body } = this.state;
    return typeof body === "string" && body.trim() !== "";
  };

  onSubmit = e => {
    const { body, isPublic } = this.state;
    e && e.preventDefault();

    if (this.isValid()) {
      this.setState({
        submitting: true
      });

      this.props
        .onSubmit({
          id: get(this.props.comment, "id"),
          serviceId: this.props.serviceId,
          input: { body, public: isPublic }
        })
        .then(() => {
          this.setState({
            body: ""
          });
          this.removeStateFromLocalStorage();
        })
        .catch(error => {
          console.log("there was an error sending the query", error);
        })
        .then(() => {
          this.setState({ submitting: false });
        });
    }
  };

  getStateFromLocalStorage = () => {
    const { serviceId } = this.props.match.params;
    if (!this.props.comment && serviceId && window.localStorage) {
      const currentDraft = window.localStorage.getItem(
        `commentDraft:${serviceId}`
      );
      if (currentDraft) {
        this.setState({ ...JSON.parse(currentDraft) });
      }
    }
  };

  saveStateToLocalStorage = debounce(() => {
    const { serviceId } = this.props.match.params;
    if (!this.props.comment && serviceId && window.localStorage) {
      const { body, isPublic } = this.state;
      window.localStorage.setItem(
        `commentDraft:${serviceId}`,
        JSON.stringify({ body, isPublic })
      );
    }
  }, 500);

  removeStateFromLocalStorage = () => {
    const { serviceId } = this.props.match.params;
    if (!this.props.comment && serviceId && window.localStorage) {
      window.localStorage.removeItem(`commentDraft:${serviceId}`);
    }
  };

  updateBody = e => {
    const body = e.target.value;
    this.setState({ body });
    this.saveStateToLocalStorage();
  };

  setPrivacy = isPublic => {
    this.setState({ isPublic });
    this.bodyTextArea.focus();
    this.saveStateToLocalStorage();
  };

  render() {
    const { currentUser, isAdminUser } = this.props.userData;
    const isValid = this.isValid();

    return (
      <Shortcuts name="COMMENT_FORM" handler={this.handleShortcuts}>
        <CommentForm onSubmit={this.onSubmit}>
          <Avatar
            email={currentUser ? currentUser.email : ""}
            style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
          />
          <FormBody isPublic={this.state.isPublic}>
            {isAdminUser ? (
              <FormHeader>
                <div style={{ marginBottom: -1 }}>
                  <FormTab
                    isPublic={this.state.isPublic}
                    active={this.state.isPublic}
                    onClick={() => this.setPrivacy(true)}
                  >
                    Public
                  </FormTab>
                  <FormTab
                    isPublic={this.state.isPublic}
                    active={!this.state.isPublic}
                    onClick={() => this.setPrivacy(false)}
                  >
                    Private
                  </FormTab>
                </div>
              </FormHeader>
            ) : (
              <FormHeader>
                <div style={{ marginBottom: -1 }}>
                  <FormTab
                    isPublic={this.state.isPublic}
                    active={this.state.isPublic}
                    onClick={() => this.setPrivacy(true)}
                  >
                    New Comment
                  </FormTab>
                </div>
              </FormHeader>
            )}
            <FormCommentBody>
              <TextArea
                value={this.state.body}
                placeholder="Leave a comment"
                onChange={this.updateBody}
                rows={5}
                innerRef={textarea => {
                  this.bodyTextArea = textarea;
                }}
              />
              <FormActions>
                <Button
                  variant="primary"
                  disabled={!isValid || this.state.submitting}
                  type="submit"
                >
                  {this.state.submitting ? "Submitting comment..." : "Comment"}
                </Button>
              </FormActions>
            </FormCommentBody>
          </FormBody>
        </CommentForm>
      </Shortcuts>
    );
  }
}

const enhance = compose(withRouter, withCurrentUser());
export default enhance(CommentFormContainer);



// WEBPACK FOOTER //
// ./src/components/activities/CommentForm.js